import { DescriptionOutlined, FolderOutlined } from "@mui/icons-material"
import { Box, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { formatArtifactSize } from "helpers/formatters/file-size"
import { basenameOf, directoryOf } from "./skill-package-paths"
import { WIDE_EXPLORER_QUERY } from "./skill-package-layout"
import type { SkillPackageEntry } from "./skill-package-contents"

const ROOT_SX = { display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 } satisfies SxProps<Theme>
const HEADING_SX = {
  display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", px: 1, pb: 0.5,
  fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "text.secondary",
} satisfies SxProps<Theme>
const TOTAL_SX = { fontWeight: 400, letterSpacing: 0, textTransform: "none" } satisfies SxProps<Theme>
const LIST_SX = { display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 } satisfies SxProps<Theme>
const FOLDER_SX = { display: "none", alignItems: "center", gap: 0.75, px: 1, pt: 1, pb: 0.25, color: "text.secondary", [WIDE_EXPLORER_QUERY]: { display: "flex" } } satisfies SxProps<Theme>
const ROW_SX = {
  display: "flex", alignItems: "center", gap: 1, width: "100%", minWidth: 0,
  px: 1, py: 0.75, border: 0, borderRadius: 1, background: "none", font: "inherit", textAlign: "left", cursor: "pointer", color: "text.secondary",
  "&:hover": { backgroundColor: "var(--app-subtle-hover-surface)" },
  '&[aria-current="true"]': { backgroundColor: "color-mix(in srgb, var(--app-primary) 16%, transparent)", color: "text.primary" },
} satisfies SxProps<Theme>
const NESTED_ROW_SX = { [WIDE_EXPLORER_QUERY]: { pl: 3 } } satisfies SxProps<Theme>
const NAME_SX = { flex: 1, minWidth: 0, fontSize: "0.8125rem", overflowWrap: "anywhere" } satisfies SxProps<Theme>
const SELECTED_NAME_SX = { fontWeight: 700 } satisfies SxProps<Theme>
const PREFIX_SX = { color: "text.disabled", [WIDE_EXPLORER_QUERY]: { display: "none" } } satisfies SxProps<Theme>
const SIZE_SX = { flexShrink: 0, fontSize: "0.6875rem", color: "text.secondary" } satisfies SxProps<Theme>
const ICON_SX = { flexShrink: 0, fontSize: "0.875rem" } satisfies SxProps<Theme>

function FileRow({ entry, isSelected, onSelect }: { entry: SkillPackageEntry, isSelected: boolean, onSelect: (path: string) => void }) {
  const directory = directoryOf(entry.path)
  return (
    <Box
      component="button"
      type="button"
      sx={directory === "" ? ROW_SX : [ROW_SX, NESTED_ROW_SX]}
      aria-current={isSelected}
      onClick={() => onSelect(entry.path)}
      data-testid="kelly-skill-package-file"
      data-entry-path={entry.path}
    >
      <DescriptionOutlined sx={ICON_SX} />
      <Typography component="span" sx={isSelected ? [NAME_SX, SELECTED_NAME_SX] : NAME_SX}>
        {/* The full path is what the reviewer is approving; only its presentation changes between arrangements. */}
        {directory !== "" && <Box component="span" sx={PREFIX_SX} data-testid="kelly-skill-package-path-prefix">{`${directory}/`}</Box>}
        {basenameOf(entry.path)}
      </Typography>
      <Typography component="span" sx={SIZE_SX}>{formatArtifactSize(entry.sizeBytes)}</Typography>
    </Box>
  )
}

export interface SkillPackageFileListProps {
  entries: SkillPackageEntry[]
  selectedPath: string
  onSelect: (path: string) => void
  omittedEntryCount: number
}

export function SkillPackageFileList({ entries, selectedPath, onSelect, omittedEntryCount }: SkillPackageFileListProps) {
  const fileCount = entries.length + omittedEntryCount
  const listedBytes = entries.reduce((total, entry) => total + entry.sizeBytes, 0)
  const summary = omittedEntryCount > 0
    ? `${fileCount} files · ${formatArtifactSize(listedBytes)} in the ${entries.length} shown`
    : `${fileCount} ${fileCount === 1 ? "file" : "files"} · ${formatArtifactSize(listedBytes)}`

  return (
    <Box sx={ROOT_SX}>
      <Typography component="div" sx={HEADING_SX}>
        Package
        <Box component="span" sx={TOTAL_SX} data-testid="kelly-skill-package-total">
          {summary}
        </Box>
      </Typography>
      <Box sx={LIST_SX} role="list" aria-label="Files in this package" data-testid="kelly-skill-package-files">
        {entries.map((entry, index) => {
          const directory = directoryOf(entry.path)
          const isNewFolder = directory !== "" && directory !== directoryOf(entries[index - 1]?.path ?? "")
          return (
            <Box key={entry.path} role="listitem" sx={LIST_SX}>
              {isNewFolder && (
                <Typography component="span" variant="caption" sx={FOLDER_SX} data-testid="kelly-skill-package-folder">
                  <FolderOutlined sx={ICON_SX} />
                  {directory}
                </Typography>
              )}
              <FileRow entry={entry} isSelected={entry.path === selectedPath} onSelect={onSelect} />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
